import * as React from 'react'
import { ReactElement, useContext, useEffect, useState } from 'react'
import { SortDirection } from '../../config/settingConfig'
import FileContext, { FileListItemDto } from '../../context/FileContext'
import { FileStatus } from '../../config/fileConfig'
import GlobalContext from '../../context/GlobalContext'
import BaseTagPicker, { BaseTagPickerProps } from './BaseTagPicker'

type Props = Partial<BaseTagPickerProps<FileListItemDto>> &
    Required<Pick<BaseTagPickerProps<FileListItemDto>, 'selectedOptions' | 'onOptionsSelect'>> & {
        includeArchived?: boolean
    }

export default function FilePicker(props: Props): ReactElement {
    const { files } = useContext(FileContext)
    const { settings } = useContext(GlobalContext)
    const [sortedFiles, setSortedFiles] = useState<FileListItemDto[]>([])

    useEffect(() => {
        if (files.loaded) {
            const fileSort = settings.fileSort
            const asc: boolean = fileSort?.direction === SortDirection.Ascending
            setSortedFiles(
                files.data.sort(
                    (a, b) =>
                        (a[fileSort?.field] || a.reference)
                            .toLocaleString()
                            .localeCompare((b[fileSort?.field] || b.reference).toLocaleString()) * (asc ? 1 : -1),
                ),
            )
        }
    }, [files])

    return (
        <BaseTagPicker<FileListItemDto>
            entities={sortedFiles}
            filter={(entity: FileListItemDto) =>
                (props.filter?.(entity) ?? entity.status === FileStatus.Open) ||
                props.selectedOptions?.some(id => id === entity.id)
            }
            getEntityValue={file => file.id}
            getEntityLabel={(entity: FileListItemDto) =>
                settings.fileSort?.field === 'name' ? entity.name : entity.reference
            }
            getEntitySecondaryContent={(entity: FileListItemDto) =>
                settings.fileSort?.field === 'name' ? entity.reference : entity.name
            }
            getEntityTagLabel={file => file.reference}
            getEntityTagSecondaryContent={file => file.name}
            placeholderKey={'base.filepicker.placeholder'}
            {...props}
        />
    )
}
